/**
 * BARE MC — Living Journey Timeline
 * En levende miniature-rejsefilm.
 * Skov → Sø → Landsby → Bakker → Kyst → Marker → Solnedgang → Skov
 *
 * Motorcyklen er fast. Verdenen bevæger sig bagved.
 * Målet: "Jeg kører gennem en levende verden" — ikke "Jeg bruger en app".
 *
 * URL: /living-preview
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// ─── Konstanter ────────────────────────────────────────────────────────────────
const MINT = '#2DFF88';
const MXF  = 0.38;  // motorcyklen holdes fast ved 38% fra venstre

// Stripe: ~10.5% af iPhone 844px
const SH_FULL  = 88;
const SH_MEDIA = 26;

const BASE = 'https://static.prod-images.emergentagent.com/jobs/ec96930b-8193-4448-a796-8853d87fd03c/images/';

// 7 sidevisnings-scener — en rejse fra skov til solnedgang
// Alle billeder: side-view landskaber, IKKE første-persons vejbilleder
const SCENES = [
  BASE + '9534109a3fc3cbb9323c524475417e517c89930886d752777d85bd5912e1c7b7.png', // skov
  BASE + '6ade42e54602bf44fd2a4505c5d3ace096b49337dac8a2ee45b00427dc938e36.png', // sø
  BASE + 'b038f6b256c182bb70181906daaf466cd25d6823ff2c6d6e19c97a849b11acb0.png', // landsby
  BASE + '5e6f708766350ebab2e78732c09b17b1620d8429e64c3719c0d7a3690240fb11.png', // bakker
  BASE + '4fa377f8e4dfc1a1ff12174823b05d7e0467d1591c89daf31b8142d88814f979.png', // kyst
  BASE + '7924f972b96f24b3091d7084e8f997693e74565a1197db1841670fc8e2189139.png', // marker
  BASE + '942d1144b445ddf77f3e3eea8fbd154536a63b2da2f5fe269c1b3a9d2137cc38.png', // solnedgang
];

// Verdens totale bredde — 7 scener × ~300px step
const WORLD_W = 2100;

// BMW motorcykel — transparent PNG, vender mod højre
const MOTO_URL = BASE + 'f3684cc22c4a9abbf0752b2932dda810447a5a4e3c14066d55baad5ae4c5c97c.png';
const MOTO_H   = 28;
const MOTO_W   = Math.round(MOTO_H * 1.9);

// Media fade
const MEDIA_TOTAL_MS     = 8000;
const MEDIA_FADEIN_AT_MS = MEDIA_TOTAL_MS - 2000;
const TOTAL_MS           = 42 * 60 * 1000;

function fmtTime(ms) {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ─── Scrub hook ────────────────────────────────────────────────────────────────
function useScrub(progress, totalMs, onSeek) {
  const [dragging, setDragging]   = useState(false);
  const [scrubOff, setScrubOff]   = useState(0);
  const [scrubP,   setScrubP]     = useState(null);
  const containerRef = useRef(null);
  const [cW, setCW]  = useState(390);
  const scrubRef     = useRef(null);
  const liveRef      = useRef(progress);

  const lP  = scrubP != null ? scrubP : progress;
  liveRef.current = lP;
  const rem = Math.max(0, (totalMs || 0) - Math.round(lP * (totalMs || 0)));
  const moSX  = cW * MXF;
  const rawTX = -(lP * WORLD_W - moSX - (dragging ? scrubOff : 0));
  const tX    = Math.max(-(WORLD_W - cW), Math.min(0, rawTX));

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => setCW(e.contentRect.width || 390));
    obs.observe(el);
    setCW(el.offsetWidth || 390);
    return () => obs.disconnect();
  }, []);

  const handlePD = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    scrubRef.current = { x: e.clientX, p: liveRef.current };
  }, []);
  const handlePM = useCallback((e) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId) || !scrubRef.current) return;
    const dx = e.clientX - scrubRef.current.x;
    setScrubP(Math.max(0, Math.min(1, scrubRef.current.p + dx / cW)));
    setScrubOff(Math.max(-44, Math.min(44, dx)));
  }, [cW]);
  const handlePU = useCallback((e) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId) || !scrubRef.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const dx = e.clientX - scrubRef.current.x;
    const np = Math.max(0, Math.min(1, scrubRef.current.p + dx / cW));
    setScrubP(np); onSeek?.(np);
    setTimeout(() => setScrubP(null), 80);
    setScrubOff(0); setDragging(false); scrubRef.current = null;
  }, [cW, onSeek]);
  const handlePC = useCallback((e) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    setScrubOff(0); setScrubP(null); setDragging(false); scrubRef.current = null;
  }, []);

  return {
    containerRef, cW, lP, rem, moSX, tX,
    txT: dragging ? 'none' : 'transform 0.07s ease-out',
    dragging, handlePD, handlePM, handlePU, handlePC,
  };
}

// ─── World Canvas: 7 scener i sidevisning ──────────────────────────────────────
// Scener tegnes side om side med blød cross-fade ved overgange.
// Ingen første-persons perspektiv — kun sidevisning af levende verden.
function WorldCanvas({ tX, txT }) {
  const canvasRef = useRef(null);
  const drawnRef  = useRef(false);

  useEffect(() => {
    if (drawnRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const imgs = SCENES.map(url => {
      const i = new Image();
      i.crossOrigin = 'anonymous';
      i.src = url;
      return i;
    });

    // Hver scene tildeles ~300px af kanvasbredden
    const n       = imgs.length;
    const SCN_W   = Math.round(WORLD_W / (n - 0.5)); // ~330px
    const OVERLAP = Math.round(SCN_W * 0.18);         // 18% cross-fade overlap
    const STEP    = SCN_W - OVERLAP;                  // ~270px fremad

    // Kilde-crop: vis den sceneriske midterdel — inkl. himmel og grund
    const SRC_W   = 1264;
    const SRC_H   = 848;
    const srcTop  = Math.round(SRC_H * 0.18);  // fra 18% ned — medtager himmel
    const srcH    = Math.round(SRC_H * 0.72);  // 72% af billedets højde

    let loaded = 0;
    const tryDraw = () => {
      loaded++;
      if (loaded < imgs.length) return;
      drawnRef.current = true;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, WORLD_W, SH_FULL);

      imgs.forEach((img, i) => {
        const x       = i * STEP;
        const isFirst = i === 0;
        const isLast  = i === imgs.length - 1;

        // Tegn scene på temp canvas
        const tmp = document.createElement('canvas');
        tmp.width  = SCN_W;
        tmp.height = SH_FULL;
        const tc = tmp.getContext('2d');

        // Stræk kildeudsnit til scenebredden
        tc.drawImage(img, 0, srcTop, SRC_W, srcH, 0, 0, SCN_W, SH_FULL);

        // Alpha-maske: blød cross-fade ved kanter
        tc.globalCompositeOperation = 'destination-in';
        const FADE = OVERLAP / SCN_W;
        const grd  = tc.createLinearGradient(0, 0, SCN_W, 0);

        if (!isFirst) {
          grd.addColorStop(0,           'rgba(0,0,0,0)');
          grd.addColorStop(FADE * 0.70, 'rgba(0,0,0,1)');
        } else {
          grd.addColorStop(0, 'rgba(0,0,0,1)');
        }
        if (!isLast) {
          grd.addColorStop(1 - FADE * 0.70, 'rgba(0,0,0,1)');
          grd.addColorStop(1,               'rgba(0,0,0,0)');
        } else {
          grd.addColorStop(1, 'rgba(0,0,0,1)');
        }

        tc.fillStyle = grd;
        tc.fillRect(0, 0, SCN_W, SH_FULL);
        tc.globalCompositeOperation = 'source-over';

        ctx.drawImage(tmp, x, 0);
      });
    };

    imgs.forEach(img => {
      if (img.complete && img.naturalWidth > 0) tryDraw();
      else { img.onload = tryDraw; img.onerror = tryDraw; }
    });
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={WORLD_W}
      height={SH_FULL}
      style={{
        position: 'absolute',
        top: 0, left: 0,
        transform: `translateX(${tX.toFixed(2)}px)`,
        transition: txT,
        willChange: 'transform',
        filter: 'saturate(1.15) brightness(0.96)',
      }}
    />
  );
}

// ─── Landevej: Blød organisk GPS-kurve ─────────────────────────────────────────
function RoadLine({ cW }) {
  const yBase = SH_FULL * 0.82;
  const pts = Array.from({ length: 80 }, (_, i) => {
    const t = i / 79;
    const y = yBase
      + Math.sin(t * 4.4 + 0.4)  * 1.8
      + Math.sin(t * 9.7 + 2.1)  * 0.6
      + Math.cos(t * 2.5 + 1.3)  * 1.0;
    return `${(t * cW).toFixed(1)},${y.toFixed(1)}`;
  });
  const d = `M ${pts.join(' L ')}`;
  return (
    <svg style={{
      position: 'absolute', top: 0, left: 0,
      width: '100%', height: SH_FULL,
      pointerEvents: 'none', zIndex: 6, overflow: 'visible',
    }} viewBox={`0 0 ${cW} ${SH_FULL}`} preserveAspectRatio="none">
      <path d={d} fill="none" stroke="rgba(0,10,5,0.80)" strokeWidth="6" style={{ filter: 'blur(2px)' }} />
      <path d={d} fill="none" stroke={MINT} strokeWidth="1.1" opacity="0.50" />
      <path d={d} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="0.6" strokeDasharray="9 15" />
    </svg>
  );
}

// ─── Journey Timeline ──────────────────────────────────────────────────────────
function JourneyTimeline({ progress, totalMs, onSeek, paused, onPlayPause, mediaPlaying }) {
  const {
    containerRef, cW, lP, rem, moSX, tX, txT,
    dragging, handlePD, handlePM, handlePU, handlePC,
  } = useScrub(progress, totalMs, onSeek);

  const stripH   = mediaPlaying ? SH_MEDIA : SH_FULL;
  const motoH    = mediaPlaying ? 12 : MOTO_H;
  const motoW    = mediaPlaying ? Math.round(12 * 1.9) : MOTO_W;
  const motoLeft = moSX - motoW * 0.5;

  return (
    <div data-testid="journey-timeline" style={{ paddingBottom: 'env(safe-area-inset-bottom,0px)' }}>
      <div
        ref={containerRef}
        data-testid="timeline-strip"
        style={{
          position: 'relative', width: '100%', height: stripH, overflow: 'hidden',
          cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none',
          transition: 'height 700ms cubic-bezier(0.4,0,0.2,1)',
          background: '#000a03',
        }}
        onPointerDown={handlePD} onPointerMove={handlePM}
        onPointerUp={handlePU}   onPointerCancel={handlePC}
      >
        {/* Verden scroller bagved motorcyklen */}
        <WorldCanvas tX={tX} txT={txT} />

        {/* Top-fade: strimlen smelter ind i video-indholdet */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '40%',
          background: 'linear-gradient(to bottom, rgba(0,10,3,0.88) 0%, transparent 100%)',
          pointerEvents: 'none', zIndex: 4,
        }} />

        {/* Side-vignetter: verden opløses blødt mod kanterne */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to right, rgba(0,10,3,0.96) 0%, rgba(0,10,3,0.75) 8%, rgba(0,10,3,0) 28%, rgba(0,10,3,0) 72%, rgba(0,10,3,0.75) 92%, rgba(0,10,3,0.96) 100%)',
          pointerEvents: 'none', zIndex: 4,
        }} />

        {/* GPS-kurve / vej */}
        <RoadLine cW={cW} />

        {/* Motorcykel — lille, naturlig skygge, vender mod højre */}
        <img
          src={MOTO_URL} alt="" draggable={false}
          style={{
            position: 'absolute', bottom: 2, left: motoLeft,
            height: motoH, width: motoW,
            objectFit: 'contain', objectPosition: 'center bottom',
            pointerEvents: 'none', userSelect: 'none', zIndex: 10,
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.95)) drop-shadow(0 1px 2px rgba(0,0,0,0.70))',
            transition: 'height 700ms ease, width 700ms ease',
          }}
        />
      </div>

      {/* Minimal kontrol — kun synlig uden media */}
      {!mediaPlaying && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '2px 14px 7px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.38) 100%)',
          position: 'relative', zIndex: 10,
        }}>
          <button
            data-testid="timeline-play-pause"
            onClick={(e) => { e.stopPropagation(); onPlayPause?.(); }}
            style={{
              width: 20, height: 20, borderRadius: '50%',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', backdropFilter: 'blur(8px)', flexShrink: 0,
            }}
          >
            {paused
              ? <svg width="5" height="7" viewBox="0 0 10 12" fill="white" opacity="0.55"><polygon points="1,1 9,6 1,11" /></svg>
              : <svg width="5" height="7" viewBox="0 0 10 12" fill="white" opacity="0.55"><rect x="1" y="1" width="3" height="10" rx="1" /><rect x="6" y="1" width="3" height="10" rx="1" /></svg>}
          </button>
          <span style={{ color: 'rgba(255,255,255,0.16)', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.06em' }}>
            {fmtTime(rem)}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Replay baggrund: blød mørk verden ─────────────────────────────────────────
// Baggrunden afspejler hvilken scene der er aktiv i timeline-strimlen.
// Dette giver en sammenhængende "levende verden" fornemmelse.
function ReplayBackground({ progress }) {
  const sceneIdx = Math.min(Math.floor(progress * SCENES.length), SCENES.length - 1);
  const bgScene  = SCENES[sceneIdx];

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      {/* Dynamisk baggrund — blurred scene svarer til hvad der er i timeline */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url(${bgScene})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 30%',
        filter: 'blur(22px) saturate(0.65) brightness(0.32)',
        transform: 'scale(1.06)',
        transition: 'background-image 0.8s ease',
      }} />

      {/* Cinematisk overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(1,12,5,0.55) 55%, rgba(1,12,5,0.92) 100%)',
      }} />

      {/* Route SVG — diskret */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <filter id="route-glow">
            <feGaussianBlur stdDeviation="5" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <path
          d="M 55,540 Q 170,450 295,405 Q 395,372 490,338 Q 578,300 668,272 Q 748,250 830,243"
          fill="none" stroke="rgba(45,255,136,0.07)" strokeWidth="3.5" filter="url(#route-glow)"
        />
        <path
          d="M 55,540 Q 170,450 295,405 Q 395,372 490,338 Q 578,300 668,272 Q 748,250 830,243"
          fill="none" stroke="rgba(45,255,136,0.18)" strokeWidth="0.9" strokeDasharray="5 10"
        />
        <circle
          cx={55  + progress * 775}
          cy={540 - progress * 297 + Math.sin(progress * 6.2) * 14}
          r="2.5" fill={MINT}
          style={{ filter: 'drop-shadow(0 0 5px #2DFF88)' }}
        />
      </svg>
    </div>
  );
}

// ─── Minimal Speed HUD ─────────────────────────────────────────────────────────
function SpeedHUD({ progress, paused }) {
  const speed = Math.round(78 + Math.sin(progress * 17) * 28);
  const dist  = (progress * 48.3).toFixed(1);
  return (
    <>
      <div style={{ position: 'absolute', top: 28, left: 20, zIndex: 20 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
          <span style={{
            color: '#2DFF88', fontSize: 34, fontWeight: 900,
            fontFamily: 'monospace', lineHeight: 1, letterSpacing: '-1px',
            textShadow: '0 0 18px rgba(45,255,136,0.55)',
          }}>
            {paused ? '--' : speed}
          </span>
          <span style={{ color: 'rgba(45,255,136,0.40)', fontSize: 11, fontWeight: 600 }}>km/t</span>
        </div>
      </div>
      <div style={{ position: 'absolute', top: 32, right: 20, zIndex: 20, textAlign: 'right' }}>
        <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 700, fontFamily: 'monospace' }}>
          {dist} <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.08em' }}>KM</span>
        </span>
      </div>
      {paused && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 20, pointerEvents: 'none' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(14px)',
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="12" height="14" viewBox="0 0 12 14" fill="white" opacity="0.80"><polygon points="1,1 11,7 1,13" /></svg>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Media check-in overlay ────────────────────────────────────────────────────
function MediaOverlay({ visible }) {
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      pointerEvents: 'none',
      opacity: visible ? 1 : 0,
      transition: 'opacity 1.8s cubic-bezier(0.4,0,0.2,1)',
    }}>
      <div style={{
        width: '70%', aspectRatio: '16/9',
        background: 'rgba(5,18,10,0.88)',
        borderRadius: 6, border: '1px solid rgba(45,255,136,0.10)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ color: 'rgba(255,255,255,0.20)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          check-in
        </span>
      </div>
    </div>
  );
}

// ─── Hovedside ─────────────────────────────────────────────────────────────────
export default function LivingTimelineConceptPage() {
  const [progress,  setProgress]  = useState(0.05);
  const [paused,    setPaused]    = useState(false);
  const [mediaPl,   setMediaPl]   = useState(false);
  const [uiOpacity, setUiOpacity] = useState(1);
  const mediaTimers = useRef([]);

  // Auto-fremskridt
  useEffect(() => {
    if (paused || mediaPl) return;
    const id = setInterval(() => {
      setProgress(p => {
        if (p >= 1) { setPaused(true); return 1; }
        return Math.min(1, p + 0.0004);
      });
    }, 200);
    return () => clearInterval(id);
  }, [paused, mediaPl]);

  // Cinematisk media-fade
  useEffect(() => {
    mediaTimers.current.forEach(clearTimeout);
    mediaTimers.current = [];
    if (mediaPl) {
      setUiOpacity(0.10);
      mediaTimers.current.push(setTimeout(() => setUiOpacity(1), MEDIA_FADEIN_AT_MS));
      mediaTimers.current.push(setTimeout(() => setMediaPl(false), MEDIA_TOTAL_MS));
    } else {
      setUiOpacity(1);
    }
    return () => mediaTimers.current.forEach(clearTimeout);
  }, [mediaPl]);

  const uiTransition = 'opacity 1.8s cubic-bezier(0.4, 0, 0.2, 1)';

  return (
    <div
      data-testid="replay-root"
      style={{ width: '100%', height: '100vh', position: 'relative', overflow: 'hidden', background: '#000a03', userSelect: 'none' }}
    >
      {/* Baggrund — blurred scene fra den aktive del af rejsen */}
      <ReplayBackground progress={progress} />

      {/* Media overlay */}
      <MediaOverlay visible={mediaPl} />

      {/* UI-lag: fadder ud under media */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 20,
        opacity: uiOpacity, transition: uiTransition,
        pointerEvents: uiOpacity < 0.35 ? 'none' : 'auto',
      }}>
        <SpeedHUD progress={progress} paused={paused} />
        <div style={{ position: 'absolute', inset: 0, cursor: 'pointer' }} onClick={() => setPaused(p => !p)} />
      </div>

      {/* Timeline strip: fadder med UI */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 88,
        opacity: uiOpacity, transition: uiTransition,
      }}>
        <JourneyTimeline
          progress={progress}
          totalMs={TOTAL_MS}
          onSeek={(p) => { setProgress(p); setPaused(false); }}
          paused={paused}
          onPlayPause={() => setPaused(v => !v)}
          mediaPlaying={mediaPl}
        />
      </div>

      {/* Media-test — altid synlig */}
      <button
        data-testid="media-toggle-btn"
        onClick={(e) => { e.stopPropagation(); setMediaPl(v => !v); }}
        style={{
          position: 'absolute', top: 14, right: 20, zIndex: 999,
          padding: '4px 13px', borderRadius: 20,
          fontSize: 10, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.06em',
          background: mediaPl ? 'rgba(45,255,136,0.18)' : 'rgba(0,0,0,0.55)',
          color: mediaPl ? MINT : 'rgba(255,255,255,0.40)',
          border: `1px solid ${mediaPl ? 'rgba(45,255,136,0.35)' : 'rgba(255,255,255,0.10)'}`,
          backdropFilter: 'blur(12px)',
          transition: 'all 0.3s ease',
          textTransform: 'uppercase',
        }}
      >
        {mediaPl ? 'Media' : 'Test media'}
      </button>
    </div>
  );
}
